import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";

import {
    deleteDeploymentHistory,
    DeploymentHistoryEntry,
    DeploymentHistoryStatus,
    fetchDeploymentHistory,
    repackageDeployment,
    transferDeployment,
} from "../../api/deployments";
import { Site } from "../../api/sites";
import { requestTransferCredentials } from "./transferCredentials";

type Props = {
    show: boolean;
    sites: Site[];
    defaultSiteId?: number | null;
    onClose: () => void;
};

const STATUS_LABELS: Record<DeploymentHistoryStatus, string> = {
    PACKAGE_CREATED: "패키지 생성",
    PACKAGE_FAILED: "생성 실패",
    TRANSFERRING: "전송 중",
    TRANSFERRED: "전송 완료",
    TRANSFER_FAILED: "전송 실패",
};

const formatDateTime = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return new Intl.DateTimeFormat("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(parsed);
};

const formatBytes = (value?: number | null) => {
    if (value == null) return "-";
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const errorMessage = (error: unknown, fallback: string) => {
    const response = (error as { response?: { data?: { message?: string; detail?: string } } })?.response;
    return response?.data?.message || response?.data?.detail || fallback;
};

export default function DeploymentHistoryModal({
    show,
    sites,
    defaultSiteId,
    onClose,
}: Props) {
    const modalRef = useRef<HTMLDivElement>(null);
    const [siteFilter, setSiteFilter] = useState<number | "ALL">("ALL");
    const [entries, setEntries] = useState<DeploymentHistoryEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);

    const siteById = useMemo(
        () => new Map(sites.map((site) => [site.id, site])),
        [sites]
    );

    const loadHistory = useCallback(async (filter: number | "ALL") => {
        setIsLoading(true);
        try {
            const result = await fetchDeploymentHistory(filter === "ALL" ? undefined : filter);
            setEntries(result.entries);
            setTotal(result.total);
        } catch (error) {
            console.error("deployment history load failed", error);
            await Swal.fire({
                icon: "error",
                title: "배포 이력 조회 실패",
                text: "저장된 배포 이력을 불러오지 못했습니다.",
            });
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!show) return;
        const initialFilter = defaultSiteId ?? "ALL";
        setSiteFilter(initialFilter);
        void loadHistory(initialFilter);
    }, [show, defaultSiteId, loadHistory]);

    useEffect(() => {
        if (!show) return;
        const handleMouseDown = (event: MouseEvent) => {
            if (!modalRef.current?.contains(event.target as Node)) onClose();
        };
        document.addEventListener("mousedown", handleMouseDown);
        return () => document.removeEventListener("mousedown", handleMouseDown);
    }, [show, onClose]);

    const handleTransfer = async (entry: DeploymentHistoryEntry) => {
        const site = entry.siteId == null ? undefined : siteById.get(entry.siteId);
        if (!site) {
            await Swal.fire({
                icon: "warning",
                title: "사이트를 찾을 수 없습니다.",
                text: "삭제되었거나 비활성화된 사이트의 이력입니다.",
            });
            return;
        }

        const credentials = await requestTransferCredentials(site);
        if (!credentials) return;

        setBusyId(entry.id);
        Swal.fire({
            title: "서버 전송 중",
            text: `${entry.packageFileName ?? "배포 패키지"}을 ${site.sshHost}에 전송하고 있습니다.`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });
        try {
            const transferred = await transferDeployment(entry.id, credentials);
            await loadHistory(siteFilter);
            await Swal.fire({
                icon: "success",
                title: "서버 전송 완료",
                text: transferred.remotePath ?? "원격 서버에 패키지를 전송했습니다.",
            });
        } catch (error) {
            await loadHistory(siteFilter);
            await Swal.fire({
                icon: "error",
                title: "서버 전송 실패",
                text: errorMessage(error, "서버 전송에 실패했습니다."),
            });
        } finally {
            setBusyId(null);
        }
    };

    const handleRepackage = async (entry: DeploymentHistoryEntry) => {
        const site = entry.siteId == null ? undefined : siteById.get(entry.siteId);
        if (!site) {
            await Swal.fire({
                icon: "warning",
                title: "사이트를 찾을 수 없습니다.",
                text: "다시 패키징하려면 이력에 연결된 배포 경로가 등록되어 있어야 합니다.",
            });
            return;
        }

        const result = await Swal.fire({
            icon: "question",
            title: "다시 패키징할까요?",
            text: "이력의 날짜·버전·파일 조건과 현재 등록된 프로젝트 경로를 사용해 새 ZIP을 생성합니다.",
            showCancelButton: true,
            confirmButtonText: "패키징",
            cancelButtonText: "취소",
            reverseButtons: true,
        });
        if (!result.isConfirmed) return;

        setBusyId(entry.id);
        Swal.fire({
            title: "패키징 중",
            text: `${site.text} 배포 패키지를 다시 생성하고 있습니다.`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
        });
        try {
            const packaged = await repackageDeployment(entry.id);
            await loadHistory(siteFilter);
            await Swal.fire({
                icon: "success",
                title: "패키징 완료",
                text: `${packaged.fileName} 파일을 바탕화면에 생성했습니다.`,
            });
        } catch (error) {
            await loadHistory(siteFilter);
            await Swal.fire({
                icon: "error",
                title: "패키징 실패",
                text: errorMessage(error, "이력의 조건으로 패키지를 다시 생성하지 못했습니다."),
            });
        } finally {
            setBusyId(null);
        }
    };

    const handleDelete = async (entry: DeploymentHistoryEntry) => {
        const result = await Swal.fire({
            icon: "question",
            title: "배포 이력을 삭제할까요?",
            text: "패키지 파일은 삭제하지 않고 이력만 제거합니다.",
            showCancelButton: true,
            confirmButtonText: "삭제",
            cancelButtonText: "취소",
        });
        if (!result.isConfirmed) return;

        setBusyId(entry.id);
        try {
            await deleteDeploymentHistory(entry.id);
            await loadHistory(siteFilter);
        } finally {
            setBusyId(null);
        }
    };

    if (!show) return null;

    return (
        <div className="modal dp-modal-backdrop" tabIndex={-1} style={{ display: "flex" }}>
            <div
                className="modal-dialog modal-dialog-centered dp-modal-dialog dp-history-dialog"
                ref={modalRef}
            >
                <div className="modal-content dp-modal-content">
                    <div className="modal-header dp-modal-header dp-history-header">
                        <div>
                            <h5 className="modal-title">배포 이력</h5>
                            <span className="dp-history-total">총 {total}건</span>
                        </div>
                        <div className="dp-history-header-actions">
                            <select
                                className="form-select"
                                value={siteFilter}
                                onChange={(event) => {
                                    const next =
                                        event.target.value === "ALL"
                                            ? "ALL"
                                            : Number(event.target.value);
                                    setSiteFilter(next);
                                    void loadHistory(next);
                                }}
                            >
                                <option value="ALL">전체 사이트</option>
                                {sites.map((site) => (
                                    <option key={site.id} value={site.id}>
                                        {site.text}
                                    </option>
                                ))}
                            </select>
                            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                        </div>
                    </div>

                    <div className="modal-body dp-modal-body dp-history-body">
                        {isLoading ? (
                            <div className="dp-history-empty">배포 이력을 불러오는 중입니다.</div>
                        ) : entries.length === 0 ? (
                            <div className="dp-history-empty">저장된 배포 이력이 없습니다.</div>
                        ) : (
                            <div className="dp-history-list">
                                {entries.map((entry) => (
                                    <article className="dp-history-row" key={entry.id}>
                                        <div className="dp-history-primary">
                                            <div className="dp-history-title-line">
                                                <strong>{entry.siteName ?? "사이트 미지정"}</strong>
                                                <span className={`dp-history-status is-${entry.status.toLowerCase()}`}>
                                                    {STATUS_LABELS[entry.status]}
                                                </span>
                                                <span className="dp-history-vcs">{entry.vcsType}</span>
                                            </div>
                                            <div className="dp-history-package">
                                                {entry.packageFileName ?? entry.failureMessage ?? "패키지 정보 없음"}
                                            </div>
                                            <div className="dp-history-meta">
                                                <span>{formatDateTime(entry.createdAt)}</span>
                                                <span>{entry.selectedFiles.length}개 파일</span>
                                                <span>{formatBytes(entry.packageSize)}</span>
                                                {entry.packagingElapsedMs != null && (
                                                    <span>{(entry.packagingElapsedMs / 1000).toFixed(2)}초</span>
                                                )}
                                            </div>
                                            {(entry.remotePath || entry.failureMessage) && (
                                                <div
                                                    className={
                                                        entry.failureMessage
                                                            ? "dp-history-detail is-error"
                                                            : "dp-history-detail"
                                                    }
                                                >
                                                    {entry.failureMessage ?? entry.remotePath}
                                                </div>
                                            )}
                                        </div>
                                        <div className="dp-history-actions">
                                            <button
                                                type="button"
                                                className="dp-history-repackage"
                                                disabled={busyId === entry.id || entry.status === "TRANSFERRING"}
                                                onClick={() => handleRepackage(entry)}
                                            >
                                                다시 패키징
                                            </button>
                                            {entry.packagePath && entry.status !== "PACKAGE_FAILED" && (
                                                <button
                                                    type="button"
                                                    className="dp-history-transfer"
                                                    disabled={busyId === entry.id || entry.status === "TRANSFERRING"}
                                                    onClick={() => handleTransfer(entry)}
                                                >
                                                    {entry.status === "TRANSFERRED" ? "재전송" : "서버 전송"}
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="dp-history-delete"
                                                disabled={busyId === entry.id || entry.status === "TRANSFERRING"}
                                                onClick={() => handleDelete(entry)}
                                            >
                                                삭제
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
